import {
  getContactInfoStringByEmail,
  getFullNameByNumber,
  getRecipientsWithNames,
  normalizeNumber,
} from 'app/pages/loanApplication/tabs/activities/Activities.utils';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { ActivityType } from 'constants/activities';
import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { FeatureFlag } from 'constants/featureFlags';
import { ActivitiesSubtabRouteParam } from 'constants/loanApplication';
import { callContactsMapper, contactsMapper, getActivityContactLookup } from 'utils/activities';
import { normalizeViewPhoneNumber } from 'utils/data';
import { getPersonFullName } from 'utils/loanApplication';
import { caseInsensitiveIncludes } from 'utils/string';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { selectFeatureFlagByKey } from 'store/global/selectors';
import { selectLoanApplication } from 'store/loanApplications/selectors/loanApplication.selectors';
import { getDebouncedSearchValue } from 'store/search/selectors';

import { selectExtendedBorrowers } from './borrowers.selectors';
import {
  selectAllContacts,
  selectApplicationContacts,
  selectLookedUpContactValues,
} from './contacts.selectors';
import { selectLeadFromApplication } from './lead.selectors';

const selectActivitiesFromApplication = createSelector(
  selectLoanApplication,
  ({ activities }) => activities,
);

export const selectEmailsCount = createSelector(
  selectActivitiesFromApplication,
  activities => _.filter(activities, ({ type }) => type === ActivityType.EMAIL)?.length,
);

export const selectCallEmployeeIds = createSelector(selectActivitiesFromApplication, activities => {
  const calls = _.filter(activities, ({ type }) => type === ActivityType.CALL);
  const userIds = _.map(calls, ({ employeeUserId }) => employeeUserId);

  return _.uniq(userIds);
});

const selectActivitiesWithGroupedEmails = createSelector(
  selectActivitiesFromApplication,
  activities => {
    return Object.values(activities).reduce((acc, activity) => {
      if (activity.type === ActivityType.EMAIL) {
        const existingThread = acc.find(({ threadId }) => threadId === activity.threadId);

        if (existingThread) {
          const updatedThread = {
            ...existingThread,
            emails: [...existingThread.emails, activity],
            activityDateTime:
              activity.activityDateTime > existingThread.activityDateTime
                ? activity.activityDateTime
                : existingThread.activityDateTime,
          };
          const existingThreadIndex = acc.indexOf(existingThread);
          acc[existingThreadIndex] = updatedThread;

          return acc;
        }

        acc.push({
          threadId: activity.threadId,
          emails: [activity],
          type: ActivityType.EMAIL,
          activityDateTime: activity.activityDateTime,
        });

        return acc;
      }

      acc.push(activity);

      return acc;
    }, []);
  },
);

export const selectActivityById = memoizeOne(id =>
  createSelector(selectActivitiesFromApplication, activities => activities[id]),
);

export const selectBorrowersContacts = createSelector(
  selectExtendedBorrowers,
  selectLeadFromApplication,
  (loanBorrowers, lead) => {
    if (loanBorrowers.length) {
      return loanBorrowers.map(({ fullName, email, cellPhoneNumber }) => ({
        fullName,
        emailAddress: email,
        phoneNumber: normalizeViewPhoneNumber(cellPhoneNumber),
      }));
    }

    return [
      {
        fullName: getPersonFullName(lead),
        emailAddress: lead?.emailAddress,
        phoneNumber: normalizeViewPhoneNumber(lead?.phoneNumber),
      },
    ];
  },
);

export const selectApplicationContactsWithBorrowers = createSelector(
  selectApplicationContacts,
  selectBorrowersContacts,
  contactsMapper,
);

export const selectApplicationCallContacts = createSelector(
  [selectApplicationContacts, selectBorrowersContacts],
  callContactsMapper,
);

export const selectHasContactsForSuggestion = createSelector(
  selectApplicationContactsWithBorrowers,
  contacts => !!contacts.length,
);

export const selectContactsWithBorrowers = createSelector(
  selectAllContacts,
  selectBorrowersContacts,
  contactsMapper,
);

export const selectActivityContactLookup = createSelector(
  selectActivitiesFromApplication,
  selectContactsWithBorrowers,
  selectLookedUpContactValues,
  getActivityContactLookup,
);

export const selectFilteredActivities = createSelector(
  selectActivitiesWithGroupedEmails,
  selectContactsWithBorrowers,
  getDebouncedSearchValue,
  selectFeatureFlagByKey(FeatureFlag.EnableLightWeightEmailBody),
  (activities, contacts, searchValue, isLightWeightEmailBodyAllowed) => {
    const search = _.trim(searchValue);

    if (!search) {
      return activities;
    }

    return activities.filter(activity => {
      switch (activity.type) {
        case ActivityType.EMAIL: {
          return isLightWeightEmailBodyAllowed
            ? true
            : activity.emails.some(
                ({ to, from, cc, bcc, subject, bodyString, attachmentNames }) => {
                  const fromString = getContactInfoStringByEmail(contacts, from);
                  const toString = getRecipientsWithNames({
                    contacts,
                    receivers: to,
                    separator: '\n',
                  });
                  const ccString = getRecipientsWithNames({
                    contacts,
                    receivers: cc,
                    separator: '\n',
                  });
                  const bccString = getRecipientsWithNames({
                    contacts,
                    receivers: bcc,
                    separator: '\n',
                  });
                  const attachmentsString = attachmentNames
                    .map(attach => decodeURIComponent(attach))
                    .join('\n');

                  return (
                    caseInsensitiveIncludes(fromString, search) ||
                    caseInsensitiveIncludes(toString, search) ||
                    caseInsensitiveIncludes(ccString, search) ||
                    caseInsensitiveIncludes(bccString, search) ||
                    caseInsensitiveIncludes(subject, search) ||
                    caseInsensitiveIncludes(attachmentsString, search) ||
                    (bodyString && caseInsensitiveIncludes(bodyString, search))
                  );
                },
              );
        }

        case ActivityType.CALL: {
          const fullNameTo =
            activity.fullNameTo || getFullNameByNumber(contacts, activity.phoneNumberTo) || '';
          const fullNameFrom =
            activity.fullNameFrom || getFullNameByNumber(contacts, activity.phoneNumberFrom) || '';
          const callTranscription = activity.callTranscription || '';
          const callNotes = activity.callNotes || '';

          return (
            caseInsensitiveIncludes(fullNameTo, search) ||
            caseInsensitiveIncludes(fullNameFrom, search) ||
            caseInsensitiveIncludes(normalizeNumber(activity.phoneNumberTo), search) ||
            caseInsensitiveIncludes(normalizeNumber(activity.phoneNumberFrom), search) ||
            caseInsensitiveIncludes(callTranscription, search) ||
            caseInsensitiveIncludes(callNotes, search)
          );
        }

        default:
          return false;
      }
    });
  },
);

export const selectFilteredActivitiesSubtabs = createSelector(
  selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_CALLS),
  isCallsAllowed =>
    _.filter(ActivitiesSubtabRouteParam, subtab => {
      switch (subtab) {
        case ActivitiesSubtabRouteParam.ALL:
        case ActivitiesSubtabRouteParam.EMAILS:
          return true;
        case ActivitiesSubtabRouteParam.CALLS:
          return isCallsAllowed;

        default:
          return null;
      }
    }),
);
